<script setup lang="ts">
import { computed } from 'vue';
import ActionItem from "@/components/ActionArea/ActionItem/ActionItem.vue";
import {OpenaiSkill, SpecialAction, SkillTypeEnum, WorldItem} from "@/api/dx-backend";
import abilityAvailability from "@/services/abilityAvailabilityService.js";

interface Props {
  skills?: OpenaiSkill[];
  items?: WorldItem[];
  specials?: SpecialAction[];
  playerService?: any;
  currentPhase?: string | null;
  /** The ground refuses violence: every offensive ability is greyed out and
   *  a click on one explains itself instead of arming a blow the server
   *  would refuse. */
  safeZone?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  skills: () => [],
  items: () => [],
  specials: () => [],
  playerService: null,
  currentPhase: null,
  safeZone: false
});

const emit = defineEmits<{
  skillSelected: [skill: OpenaiSkill];
  itemSelected: [item: WorldItem];
  specialSelected: [special: SpecialAction];
  actionHovered: [preview: { icon: string; name: string; skillType?: string; cost?: { kind: string; value: number }[] }];
  actionHoverEnd: [];
  offenceRefused: [];
}>();

const MAX_SKILLS = 40;
const MAX_ITEMS = 12;
const MAX_SPECIAL = 8;

const barredHere = (offensive: boolean): boolean => props.safeZone && offensive;

const skillBarred = (skill: OpenaiSkill): boolean =>
    barredHere(abilityAvailability.isOffensiveSkill(skill));

const itemBarred = (item: WorldItem): boolean =>
    barredHere(abilityAvailability.isOffensiveItem(item));

const specialBarred = (special: SpecialAction): boolean =>
    barredHere(abilityAvailability.isOffensiveSpecial(special));

const canPerformSkillAction = (skill: OpenaiSkill): boolean =>
    !skillBarred(skill)
    && abilityAvailability.canUseSkill(skill, props.playerService, props.currentPhase);

const canPerformItemAction = (item: WorldItem): boolean =>
    !itemBarred(item)
    && abilityAvailability.canUseItem(item, props.playerService, props.currentPhase);

const canPerformSpecialAction = (special: SpecialAction): boolean =>
    !specialBarred(special)
    && abilityAvailability.canUseSpecial(special, props.playerService, props.currentPhase);

// Filter items to only show those with charges > 0
const filteredItems = computed(() => {
  return props.items.filter(item => {
    // Check if item has charges_left property and it's > 0
    return item.charges_left > 0;
  });
});

const emptySkillSlots = computed(() => Math.max(0, MAX_SKILLS - props.skills.length));
const emptyItemSlots = computed(() => Math.max(0, MAX_ITEMS - filteredItems.value.length));
const emptySpecialSlots = computed(() => Math.max(0, MAX_SPECIAL - props.specials.length));

const handleSkillAction = (skillId: string) => {
  const skill = props.skills.find(s => s.id.toString() === skillId);
  if (!skill) return;
  if (skillBarred(skill)) return emit('offenceRefused');
  if (canPerformSkillAction(skill)) emit('skillSelected', skill);
};

const handleItemAction = (itemId: string) => {
  const item = filteredItems.value.find(i => i.id === itemId);
  if (!item) return;
  if (itemBarred(item)) return emit('offenceRefused');
  if (canPerformItemAction(item)) emit('itemSelected', item);
};

const handleSpecialAction = (specialType: string) => {
  const special = props.specials.find(s => s.action_type === specialType);
  if (!special) return;
  if (specialBarred(special)) return emit('offenceRefused');
  if (canPerformSpecialAction(special)) emit('specialSelected', special);
};

const handleSkillHover = (skillId: string) => {
  const skill = props.skills.find(s => s.id.toString() === skillId);
  if (skill && canPerformSkillAction(skill)) {
    emit('actionHovered', {
      icon: skill.icon || '',
      name: skill.name,
      skillType: skill.type || SkillTypeEnum.Utility,
      cost: abilityAvailability.costOf(skill),
    });
  }
};

const handleItemHover = (itemId: string) => {
  const item = filteredItems.value.find(i => i.id === itemId);
  if (item && canPerformItemAction(item)) {
    emit('actionHovered', {
      icon: item.item.icon || '',
      name: item.item.name,
      skillType: item.item.skill?.type || SkillTypeEnum.Utility,
      cost: abilityAvailability.costOf(item),
    });
  }
};

const handleSpecialHover = (specialType: string) => {
  const special = props.specials.find(s => s.action_type === specialType);
  if (special && canPerformSpecialAction(special)) {
    emit('actionHovered', {
      icon: special.icon || '',
      name: special.name || special.action_type,
      skillType: SkillTypeEnum.Special,
      cost: abilityAvailability.costOf(special),
    });
  }
};

const handleHoverEnd = () => {
  emit('actionHoverEnd');
};

</script>

<template>
  <div class="action-holder">
    <div class="action-holder__skills">
      <ActionItem
          v-for="skill in skills"
          :key="`skill-${skill.id}`"
          :id="skill.id.toString()"
          :image="skill.icon || ''"
          :title="skill.name"
          :cta-type="skill.type || SkillTypeEnum.Utility"
          :cost="skill.cost || []"
          :disabled="!canPerformSkillAction(skill)"
          @select="() => handleSkillAction(skill.id.toString())"
          @hover="() => handleSkillHover(skill.id.toString())"
          @hover-end="handleHoverEnd"
      />
      <ActionItem
          v-for="i in emptySkillSlots"
          :key="`empty-skill-${i}`"
          :id="''"
          class="action-holder__item action-holder__item--empty"
      />
    </div>

    <div class="action-holder__separator"></div>

    <div class="action-holder__items">
      <ActionItem
          v-for="item in filteredItems"
          :key="`item-${item.id}`"
          :id="item.id"
          :image="item.item.icon || ''"
          :title="item.item.name"
          :cta-type="item.item.skill.type || SkillTypeEnum.Utility"
          :cost="item.item.skill?.cost || []"
          :disabled="!canPerformItemAction(item)"
          @select="() => handleItemAction(item.id)"
          @hover="() => handleItemHover(item.id)"
          @hover-end="handleHoverEnd"
      />
      <ActionItem
          v-for="i in emptyItemSlots"
          :key="`empty-item-${i}`"
          :id="''"
          class="action-holder__item action-holder__item--empty"
      />
    </div>

    <div class="action-holder__separator"></div>

    <div class="action-holder__special">
      <ActionItem
          v-for="specialAction in specials"
          :key="`special-${specialAction.action_type}`"
          :id="specialAction.action_type"
          :image="specialAction.icon || ''"
          :title="specialAction.name || specialAction.action_type"
          :cta-type="SkillTypeEnum.Special"
          :cost="specialAction.cost || []"
          :disabled="!canPerformSpecialAction(specialAction)"
          @select="() => handleSpecialAction(specialAction.action_type)"
          @hover="() => handleSpecialHover(specialAction.action_type)"
          @hover-end="handleHoverEnd"
      />
      <ActionItem
          v-for="i in emptySpecialSlots"
          :key="`empty-special-${i}`"
          :id="''"
          class="action-holder__item action-holder__item--empty"
      />
    </div>
  </div>
</template>

<style scoped>
.action-holder {
  background: url("@/assets/images/action-area/Area.png") no-repeat center center;
  background-size: cover;
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: flex-end;
  justify-content: center;
  width: 51.75rem;
  height: 15.7rem;
  padding-bottom: 0.5rem;
}

.action-holder__item--empty {
  pointer-events: none;
  opacity: 0.3;
}

.action-holder__skills {
  width: 30rem;
  height: 12.6rem;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
  flex-wrap: wrap;
}

.action-holder__items {
  width: 9rem;
  height: 12.6rem;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
  flex-wrap: wrap;
}

.action-holder__special {
  width: 6rem;
  height: 12.6rem;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
  flex-wrap: wrap;
}

.action-holder__separator {
  background: url("@/assets/images/action-area/Delimiter.png") no-repeat center center;
  background-size: cover;
  width: 3rem;
  height: 12.6rem;
  z-index: 10;
  margin: 0 -1rem;
}
</style>